import { useEffect, useRef, useState } from 'react';
import { DBRequest, useConfig } from '../../api/api';
import {
  Box,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { GantDay } from './gant/GantDay';
import { getTimelineDays } from './gant/getTimelineDays';
import { GantPhase } from './gant/GantPhase';
import { updateOrCreate } from '../special/updateOrCreate';
import { tokens } from '../../global/theme/tokens';
import { ExoSkeleton } from './ExoSkeleton';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import FlagIcon from '@mui/icons-material/Flag';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import { t } from 'i18next';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import moment from 'moment';

export const ExoGant = ({
  projectId,
  inEditMode,
  resolution = 1,
  editable,
  ownData,
  useOwnData,
  latestStart,
  startAllowedStatuses = [0, 2],
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();

  const scrollContainer = useRef(null);

  const [apiData, setApiData] = useState(null);
  const [days, setDays] = useState(null);
  const [fullScreen, setFullScreen] = useState(false);
  const [allowedStatuses, setAllowedStatuses] = useState(startAllowedStatuses); // hide wps with a certaint status

  useEffect(() => {
    if (useOwnData) return setApiData(ownData);

    DBRequest({
      config,
      path: `projects/${projectId}/phases`,
      onResponse: handleResponse,
    });
  }, [config, ownData]);

  function handleResponse(res) {
    setApiData(res);
  }

  useEffect(() => {
    if (apiData && allowedStatuses)
      setDays(getTimelineDays(apiData.data, allowedStatuses));
  }, [apiData, allowedStatuses]);

  function handlePhaseChange(newPhase) {
    setApiData(updateOrCreate(apiData, newPhase));
  }

  // responsive
  const tableRef = useRef(null);
  const [height, setHeight] = useState(null);

  // Function to sync container height
  const syncContainerHeight = () => {
    if (tableRef.current) {
      setHeight(`${tableRef.current.offsetHeight}px`);
    }
  };

  // observe dynamic size changes
  useEffect(() => {
    if (!tableRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      syncContainerHeight();
    });

    resizeObserver.observe(tableRef.current);

    return () => resizeObserver.disconnect();
  }, [tableRef.current, days]);

  const dayRef = useRef(null);
  const [dayWidth, setDayWidth] = useState(80); // default

  function handleResize(width) {
    setDayWidth(width);
  }

  useEffect(() => {
    const container = dayRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const width = entry.contentRect.width;
        handleResize(width);
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.unobserve(container);
      resizeObserver.disconnect();
    };
  }, [dayRef, dayRef.current, days]);

  // overview
  const infoColSpan = 5;

  useEffect(() => {
    scrollToToday(days);
  }, [days, scrollContainer.current, dayWidth]);

  const scrollToToday = (days) => {
    if (scrollContainer.current && days) {
      // find the current Day index
      const index = days.findIndex(
        (obj) => obj.date.format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')
      );

      scrollContainer.current.scrollLeft = index * dayWidth - 30; // move right
    }
  };

  const realUsedDaysCount = getTimelineDays(
    apiData?.data || [],
    allowedStatuses,
    true
  ).length;

  if (!days || !apiData)
    return (
      <Box
        className="backdrop-blur-md rounded-lg flex flex-col h-full overflow-hidden"
        sx={{ bgcolor: colors.glass }}
      >
        <ExoSkeleton n={1} size="56px" borderRadius="0px" />
        <Box className="py-2 flex flex-col px-1 gap-1">
          <Box className="pb-2">
            <ExoSkeleton n={1} size="23px" width="180px" />
          </Box>
          <Box className=" pl-[0%] pr-[75%]">
            <ExoSkeleton n={1} size="50px" />
          </Box>
          <Box className=" pl-[20%] pr-[60%]">
            <ExoSkeleton n={1} size="50px" />
          </Box>
          <Box className=" pl-[40%] pr-[20%]">
            <ExoSkeleton n={1} size="50px" />
          </Box>
          <Box className=" pl-[80%] pr-[0%]">
            <ExoSkeleton n={1} size="50px" />
          </Box>
        </Box>
        <Box className="py-2 flex flex-col px-1 gap-1 pt-2">
          <Box className="pb-2">
            <ExoSkeleton n={1} size="23px" width="180px" />
          </Box>
          <Box className=" pl-[0%] pr-[35%]">
            <ExoSkeleton n={1} size="50px" />
          </Box>
          <Box className=" pl-[60%] pr-[10%]">
            <ExoSkeleton n={1} size="50px" />
          </Box>
        </Box>
      </Box>
    );

  return (
    <Box
      className="flex flex-col h-full relative w-full left-0 top-0 z-10 overflow-y-auto"
      sx={{
        position: fullScreen ? 'fixed' : 'relative',
        td: {
          verticalAlign: 'top',
        },
      }}
    >
      {fullScreen && (
        <Box
          sx={{ height: '60px', bgcolor: colors.glass }}
          className=" backdrop-blur-lg"
        />
      )}
      {realUsedDaysCount === 0 ? (
        <Box
          className="backdrop-blur-md rounded-lg flex flex-col h-full overflow-hidden items-center justify-center"
          sx={{ bgcolor: colors.glass }}
        >
          <Typography fontSize={25} fontWeight={500} className="opacity-50">
            {t('There are no scheduled work packages.')}
          </Typography>
          <Typography className="opacity-50">
            {t('Create a scheduled work package in the project tree view now.')}
          </Typography>
        </Box>
      ) : (
        <Box
          className="backdrop-blur-md rounded-lg flex flex-col h-full top-0 left-0  overflow-x-auto overflow-y-auto w-full"
          ref={scrollContainer}
        >
          <table
            ref={tableRef}
            className="h-full overflow-x-visible"
            style={{
              minWidth: dayWidth * days?.length + infoColSpan * dayWidth + 10,
            }}
          >
            <tbody>
              <tr>
                <td
                  className="hidden lg:table-cell"
                  style={{ backgroundColor: colors.glass }}
                ></td>
                <td
                  className="hidden lg:table-cell"
                  style={{ backgroundColor: colors.glass }}
                ></td>
                <td colSpan={999} style={{ height: '56px' }}></td>
              </tr>
              {apiData?.data.map((phase, index) => (
                <GantPhase
                  key={phase.id}
                  phase={phase}
                  isFirst={index === 0}
                  dayWidth={dayWidth}
                  days={days}
                  onChange={handlePhaseChange}
                  inEditMode={inEditMode}
                  projectId={projectId}
                  resolution={resolution}
                  allowedStatuses={allowedStatuses}
                  editable={editable}
                  infoWidth={infoColSpan * dayWidth}
                  latestStart={latestStart}
                />
              ))}
              <tr>
                <td
                  style={{
                    width: infoColSpan * dayWidth,
                    minWidth: infoColSpan * dayWidth,
                    backgroundColor: colors.glass,
                    minHeight: '60px',
                  }}
                  className="sticky left-0 z-10 backdrop-blur-sm hidden lg:table-cell h-full min-h-[60px]"
                ></td>
                <td
                  style={{ backgroundColor: colors.glass }}
                  className="hidden lg:table-cell"
                ></td>
                <td></td>
              </tr>
            </tbody>
          </table>
          <Box
            className="grid grid-flow-col h-full absolute top-0 left-0 -z-10 lg:pl-[10px]"
            sx={{
              gridAutoColumns: 'minmax(80px, 1fr)',
              minHeight: height,
            }}
          >
            <Box
              sx={{
                gridColumn: `span ${infoColSpan} / span ${infoColSpan}`,
              }}
              className="hidden lg:table-cell"
            />
            {days?.map(
              (day, index) =>
                index % resolution === 0 && (
                  <GantDay
                    key={day.date.format('YYYY-MM-DD')}
                    dayRef={index === 0 ? dayRef : null}
                    day={day}
                    isFirst={index === 0}
                    isLast={index === days.length - 1}
                  />
                )
            )}
          </Box>
        </Box>
      )}

      <Box className="absolute bottom-12 md:bottom-2 left-1 z-10">
        <ToggleButtonGroup
          size="small"
          value={allowedStatuses}
          onChange={(e, value) => setAllowedStatuses(value)}
          aria-label="WP Status"
          className="backdrop-blur-lg"
        >
          <Tooltip title={t('Running')}>
            <ToggleButton value={0}>
              <DirectionsRunIcon />
            </ToggleButton>
          </Tooltip>
          <Tooltip title={t('Infinite')}>
            <ToggleButton value={2}>
              <AllInclusiveIcon />
            </ToggleButton>
          </Tooltip>
          <Tooltip title={t('Finished')}>
            <ToggleButton value={1}>
              <FlagIcon />
            </ToggleButton>
          </Tooltip>
          <Tooltip title={t('Canceled')}>
            <ToggleButton value={3}>
              <ReportGmailerrorredIcon />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Box>

      <IconButton
        onClick={() => setFullScreen(!fullScreen)}
        sx={{ position: 'absolute', bgcolor: colors.card }}
        className="bottom-12 md:bottom-2 right-1 z-10 backdrop-blur-md"
      >
        {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
      </IconButton>
    </Box>
  );
};
